import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, ArrowRight, User, Users, FileCheck, Search } from "lucide-react";
import { api, loadBiodata, loadHistory, removeHistory, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History, Download, Printer, X, FileText } from "lucide-react";
import Navbar from "@/components/Navbar";

const iconByAudience = { "Dewasa": User, "Anak": Users };
const categoryColor = { "Kuasa": "bg-blue-50 text-blue-700 border-blue-200", "Pernyataan": "bg-emerald-50 text-emerald-700 border-emerald-200" };

export default function PemohonDashboard() {
  const [forms, setForms] = useState([]);
  const [q, setQ] = useState("");
  const [biodataFilled, setBiodataFilled] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/forms").then(({ data }) => setForms(data.forms || []));
    const b = loadBiodata();
    setBiodataFilled(!!(b && b.nama && b.nik));
    setHistory(loadHistory());
  }, []);

  const filtered = forms.filter((f) =>
    !q || f.title.toLowerCase().includes(q.toLowerCase()) || f.description.toLowerCase().includes(q.toLowerCase())
  );

  const deleteHistoryItem = (id) => {
    removeHistory(id);
    setHistory(loadHistory());
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Navbar variant="public" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-600 font-semibold">
            <FileCheck className="w-3.5 h-3.5" /> Dashboard Pemohon
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Halo, selamat datang.</h1>
          <p className="mt-2 text-slate-600">Pilih formulir yang ingin Anda buat. Data biodata Anda akan otomatis terisi setelah pertama kali mengisinya.</p>
        </motion.div>

        <div className="mt-8 flex flex-wrap gap-4 items-center justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cari formulir..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-11 rounded-lg bg-white"
              data-testid="search-form-input"
            />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Status biodata:</span>
            {biodataFilled ? (
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid="biodata-status-filled">Sudah tersimpan</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-600" data-testid="biodata-status-empty">Belum diisi</Badge>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((f, i) => {
            const Ico = iconByAudience[f.audience] || ClipboardList;
            return (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/formulir/${f.id}`}
                  className="form-card block rounded-2xl border border-slate-200 bg-white p-6 h-full group"
                  data-testid={`form-card-${f.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 grid place-items-center">
                      <Ico className="w-5 h-5" />
                    </div>
                    <Badge variant="outline" className={`text-xs ${categoryColor[f.category] || ''}`}>{f.category}</Badge>
                  </div>
                  <h3 className="mt-4 font-semibold text-slate-900 text-lg tracking-tight leading-snug">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-slate-400">Kode: {f.code}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium group-hover:gap-2.5 transition-all">
                      Mulai isi <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Riwayat Pemohon */}
        <section className="mt-14" data-testid="history-section">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 grid place-items-center">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 tracking-tight">Riwayat Formulir Anda</h2>
              <p className="text-xs text-slate-500">Tersimpan di perangkat ini — cetak ulang tanpa mengisi lagi.</p>
            </div>
          </div>

          {history.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center" data-testid="history-empty">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Belum ada formulir yang Anda buat. Pilih salah satu formulir di atas untuk memulai.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
              {history.map((h) => (
                <div key={h.id} className="flex flex-wrap items-center gap-3 px-5 py-4 hover:bg-slate-50/70 transition-colors" data-testid={`history-item-${h.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 text-sm leading-snug">{h.form_title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                      <span>a.n. {h.applicant_name || "—"}</span>
                      {h.kanim_name && <span>· {h.kanim_name}</span>}
                      <span>· {new Date(h.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button asChild variant="outline" size="sm" className="rounded-lg" data-testid={`history-download-${h.id}`}>
                      <a href={`${API}/submissions/${h.id}/pdf`} download={`SIMPA-${h.form_id}.pdf`}>
                        <Download className="w-4 h-4 mr-1.5" /> Unduh
                      </a>
                    </Button>
                    <Button asChild variant="ghost" size="sm" className="rounded-lg" data-testid={`history-print-${h.id}`}>
                      <a href={`${API}/submissions/${h.id}/pdf`} target="_blank" rel="noreferrer">
                        <Printer className="w-4 h-4 mr-1.5" /> Cetak
                      </a>
                    </Button>
                    <Button variant="ghost" size="sm" className="rounded-lg text-slate-400 hover:text-red-500" onClick={() => deleteHistoryItem(h.id)} data-testid={`history-delete-${h.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
